import axios from "axios";

const API_BASE = "/api/projects";

/* ---- Request deduplication: prevents duplicate simultaneous fetches ---- */
const _inflight = new Map();
function dedup(key, fetcher) {
  if (_inflight.has(key)) return _inflight.get(key);
  const promise = fetcher().finally(() => _inflight.delete(key));
  _inflight.set(key, promise);
  return promise;
}

/* Centers (districts) */
export const getCenters = () => dedup("centers", () => axios.get("/api/centers"));
export const getMyCenter = () => axios.get("/api/centers/me");
export const createCenter = (data) => axios.post("/api/centers", data);

/* Projects */
export const getProjects = () => dedup("projects", () => axios.get(API_BASE));
export const addProject = (data) => axios.post(API_BASE, data);
export const updateProject = (id, data) => axios.put(`${API_BASE}/${id}`, data);
export const getDriveDoneProjects = () => dedup("driveDone", () => axios.get("/api/projects/drive-done"));
export const getMyDriveDoneProjects = () =>
  dedup("driveDoneMine", () => axios.get("/api/projects/drive-done/mine"));
export const getLabourOffices = () => dedup("labourOffices", () => axios.get("/api/projects/labour-offices"));
export const updateOfficerLocation = (officeId, data) =>
  axios.patch(`/api/projects/labour-offices/${officeId}/officers`, data);

/* Users management */
export const getUsers = () => dedup("users", () => axios.get("/api/users"));
export const lookupUsersByIds = (ids) =>
  axios.get("/api/users/lookup", { params: { ids: ids.join(",") } });
export const resetUserPassword = (id, password) =>
  axios.post(`/api/users/${id}/reset-password`, { password });
export const deleteUser = (id) => axios.delete(`/api/users/${id}`);

/* Auth — new roles */
export const createDivisionalManager = (payload) =>
  axios.post("/api/auth/create-divisional-manager", payload); // { username, password, centers: [ids] }

export const createSupervisor = (payload) =>
  axios.post("/api/auth/create-supervisor", payload); // { username, password, centers?: [ids] }

/* CSC */
export function getCSCs() {
  return dedup("csc", () => axios.get("/api/csc"));
}

/* Unregistered workers (role-scoped on server) */
export const getAllUnregisteredWorkers = () =>
  axios.get("/api/unregisteredworkers/all");

// Make sure the backend route matches this casing.
// If your server uses lowercase, keep it lowercase here:
export const getMyUnregisteredWorkers = () =>
  axios.get("/api/unregisteredworkers/mine");

/* Auth header */
axios.interceptors.request.use((config) => {
  try {
    const raw = localStorage.getItem("kskfield_auth");
    if (raw) {
      const { token } = JSON.parse(raw);
      if (token) config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {}
  return config;
});

/* Today’s tasks (kept for compatibility) */
function getCurrentUsername() {
  const user = localStorage.getItem("kskfield_user");
  try {
    return user ? JSON.parse(user).username : "";
  } catch {
    return "";
  }
}

export const getTodaysTasks = (arg) => {
  if (typeof arg === "object" && arg !== null) {
    const { projectId, username } = arg;
    return axios.get("/api/todays-tasks", {
      params: { projectId, username: username || getCurrentUsername() },
    });
  }
  return axios.get("/api/todays-tasks", {
    params: { projectId: arg, username: getCurrentUsername() },
  });
};

export const saveTodaysTasks = (arg1, arg2) => {
  let payload;
  if (typeof arg1 === "object" && arg1 !== null) {
    payload = { ...arg1 };
  } else {
    payload = { projectId: arg1, tasks: arg2 };
  }
  const u = getCurrentUsername();
  if (u && !payload.username) payload.username = u;
  return axios.post("/api/todays-tasks/save", payload);
};
// --- Backward-compat aliases (so old imports still compile) ---
export { createDivisionalManager as createCenterHead };
export { createSupervisor as createDriveUser };
// --- ADD THIS EXPORT ---
export const getVisibleProjects = () => dedup("visibleProjects", () => axios.get("/api/projects/visible"));
// === ADD this to your api.js ===

// Update a user's centers (multi-select)
export const updateUserCenters = (id, centers) =>
  axios.put(`/api/users/${id}/centers`, { centers }); // centers: [centerId...]
export const getMyManagerName = () => axios.get("/api/users/manager-name");

/* Trackkaro vehicles */
export const getTrackkaroVehicles = () => dedup("trackkaro", () => axios.get("/api/trackkaro/vehicles"));

/* CSV -> Circle PDFs utility */
export const previewCsvPdfJob = (formData) =>
  axios.post("/api/csv-pdf/preview", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

export const getCsvPdfJob = (jobId) => axios.get(`/api/csv-pdf/jobs/${jobId}`);

export const downloadCsvPdfZip = (jobId) =>
  axios.get(`/api/csv-pdf/jobs/${jobId}/zip`, { responseType: "blob" });

export const fetchCsvPdfBlob = (jobId, pdfName) =>
  axios.get(`/api/csv-pdf/jobs/${jobId}/pdf/${encodeURIComponent(pdfName)}`, {
    responseType: "blob",
  });
